import React from 'react';
import { Award, Users, Clock, Shield, Star, ChevronLeft, ChevronRight } from '../lib/icons';
import { useState, useEffect } from 'react';
import ImageSlot from './ImageSlot';
import ContentSlot from './ContentSlot';

const About = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  const customerPhotos = [
    '/IMG_0038.jpg',
    '/IMG_0479.jpg',
    '/IMG_0517.jpg',
    '/IMG_1684.jpg',
    '/IMG_2016.jpg',
    '/IMG_2439.jpg',
    '/IMG_2512.jpg',
    '/IMG_2517.jpg',
    '/IMG_2761.jpg',
    '/IMG_3668.jpg',
    '/IMG_3793.jpg',
    '/IMG_3917.jpg',
    '/IMG_4015.jpg',
    '/IMG_4038.jpg'
  ];

  // Auto-advance carousel
  useEffect(() => {
    if (!isAutoPlaying) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prevIndex) => 
        prevIndex === customerPhotos.length - 1 ? 0 : prevIndex + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, customerPhotos.length]);

  const goToPrevious = () => {
    setCurrentIndex(currentIndex === 0 ? customerPhotos.length - 1 : currentIndex - 1);
    setIsAutoPlaying(false);
  };

  const goToNext = () => {
    setCurrentIndex(currentIndex === customerPhotos.length - 1 ? 0 : currentIndex + 1);
    setIsAutoPlaying(false);
  };

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  const stats = [
    {
      icon: Clock,
      number: '3-4',
      label: 'Hour Turnaround',
      description: 'HDMI & cleaning repairs'
    },
    {
      icon: Award,
      number: '15',
      label: 'Years Experience',
      description: 'Collective experience'
    },
    {
      icon: Shield,
      number: '100%',
      label: 'Quality Guarantee',
      description: 'On all repairs'
    },
    {
      icon: Users,
      number: '1000+',
      label: 'Happy Customers',
      description: 'Consoles repaired'
    }
  ];

  return (
    <section id="about" className="py-20 bg-[#0D0D0D]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Star className="h-8 w-8 text-[#39FF14] fill-current" />
            <Star className="h-8 w-8 text-[#39FF14] fill-current" />
            <Star className="h-8 w-8 text-[#39FF14] fill-current" />
            <Star className="h-8 w-8 text-[#39FF14] fill-current" />
            <Star className="h-8 w-8 text-[#39FF14] fill-current" />
          </div>
          <h2 className="text-4xl font-bold text-[#E0E0E0] mb-4">
            <ContentSlot slotId="about-section-heading" fallback="Why Choose GameFix Solutions?" contentType="heading" pageSection="about" label="Section Heading" />
          </h2>
          <ContentSlot
            slotId="about-section-subtitle"
            fallback="Expert repairs with unmatched turnaround times, backed by hundreds of satisfied customers"
            contentType="paragraph"
            as="p"
            pageSection="about"
            label="Section Subtitle"
            className="text-xl text-[#B0B0B0] max-w-3xl mx-auto"
          />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Content */}
          <div>
            <h3 className="text-2xl font-bold text-[#E0E0E0] mb-6">
              <ContentSlot slotId="about-expertise-heading" fallback="Our Expertise" contentType="subheading" pageSection="about" label="Expertise Heading" />
            </h3>
            <ContentSlot
              slotId="about-expertise-desc"
              fallback="We specialize in fast, professional console repairs with unmatched turnaround times. Our expert technicians complete HDMI port replacements and overheating/cleaning repairs for Xbox and PlayStation consoles in just 3-4 hours, with same-day service available."
              contentType="paragraph"
              as="p"
              pageSection="about"
              label="Expertise Description"
              className="text-lg text-[#B0B0B0] mb-6"
            />

            <div className="space-y-4 mb-8">
              <div className="flex items-start space-x-4">
                <div className="bg-[#39FF14]/20 p-2 rounded-lg">
                  <Clock className="h-6 w-6 text-[#39FF14]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#E0E0E0] mb-1">
                    <ContentSlot slotId="about-feat1-title" fallback="Lightning Fast Service" contentType="heading" pageSection="about" label="Feature 1 Title" />
                  </h3>
                  <p className="text-[#B0B0B0] text-sm">
                    <ContentSlot slotId="about-feat1-desc" fallback="HDMI & overheating repairs completed within 3-4 hours for Xbox/PlayStation" contentType="paragraph" pageSection="about" label="Feature 1 Description" />
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-[#FF1744]/20 p-2 rounded-lg">
                  <Award className="h-6 w-6 text-[#FF1744]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#E0E0E0] mb-1">
                    <ContentSlot slotId="about-feat2-title" fallback="Expert Technicians" contentType="heading" pageSection="about" label="Feature 2 Title" />
                  </h3>
                  <p className="text-[#B0B0B0] text-sm">
                    <ContentSlot slotId="about-feat2-desc" fallback="Certified professionals with extensive console repair experience" contentType="paragraph" pageSection="about" label="Feature 2 Description" />
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="bg-[#00BFFF]/20 p-2 rounded-lg">
                  <Shield className="h-6 w-6 text-[#00BFFF]" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-[#E0E0E0] mb-1">
                    <ContentSlot slotId="about-feat3-title" fallback="Quality Guarantee" contentType="heading" pageSection="about" label="Feature 3 Title" />
                  </h3>
                  <p className="text-[#B0B0B0] text-sm">
                    <ContentSlot slotId="about-feat3-desc" fallback="All repairs backed by our comprehensive warranty" contentType="paragraph" pageSection="about" label="Feature 3 Description" />
                  </p>
                </div>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat, index) => (
                <div key={index} className="text-center bg-gray-900 rounded-lg p-4 shadow-sm border border-gray-800">
                  <div className="bg-gradient-to-r from-[#FF1744] to-[#00BFFF] w-10 h-10 rounded-lg flex items-center justify-center mx-auto mb-2">
                    <stat.icon className="h-5 w-5 text-white" />
                  </div>
                  <div className="text-2xl font-bold text-[#E0E0E0] mb-1">{stat.number}</div>
                  <div className="text-sm font-semibold text-[#E0E0E0] mb-1">{stat.label}</div>
                  <div className="text-xs text-[#B0B0B0]">{stat.description}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Customer Photos Carousel */}
          <div>
            <h3 className="text-2xl font-bold text-[#E0E0E0] mb-6 text-center">
              <ContentSlot slotId="about-customers-heading" fallback="Happy Customers" contentType="subheading" pageSection="about" label="Customers Section Heading" />
            </h3>
            
            {/* Carousel */}
            <div className="relative overflow-hidden rounded-2xl shadow-2xl bg-gray-900 border border-gray-800 mb-6">
              <div 
                className="flex transition-transform duration-500 ease-in-out"
                style={{ transform: `translateX(-${currentIndex * 100}%)` }}
              >
                {customerPhotos.map((photo, index) => (
                  <div key={index} className="w-full flex-shrink-0">
                    <img
                      src={photo}
                      alt={`Repaired console ${index + 1}`}
                      className="w-full h-[400px] object-cover"
                      loading="lazy"
                    />
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              <button
                onClick={goToPrevious}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-200 backdrop-blur-sm"
                aria-label="Previous image"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              
              <button
                onClick={goToNext}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-2 rounded-full transition-all duration-200 backdrop-blur-sm"
                aria-label="Next image"
              >
                <ChevronRight className="h-5 w-5" />
              </button>

              {/* Image Counter */}
              <div className="absolute bottom-4 left-4 bg-black/50 text-white px-3 py-1 rounded-full text-sm backdrop-blur-sm">
                {currentIndex + 1} / {customerPhotos.length}
              </div>
            </div>

            {/* Dot Indicators */}
            <div className="flex justify-center mb-6 space-x-1">
              {customerPhotos.slice(0, 8).map((_, index) => (
                <button
                  key={index}
                  onClick={() => goToSlide(index)}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    index === currentIndex 
                      ? 'bg-[#00BFFF] scale-125' 
                      : 'bg-gray-600 hover:bg-gray-500'
                  }`}
                  aria-label={`Go to image ${index + 1}`}
                />
              ))}
              <span className="text-[#B0B0B0] text-xs self-center ml-2">+{customerPhotos.length - 8} more</span>
            </div>

            {/* Testimonial */}
            <div className="bg-gray-900/80 backdrop-blur-sm rounded-xl p-6 border border-gray-800">
              <blockquote className="text-[#E0E0E0] mb-4 italic text-center">
                <ContentSlot slotId="about-testimonial-quote" fallback="&quot;GameFix Solutions saved my PS5! HDMI port was completely broken and they had it fixed in 4 hours. Professional service and fair pricing - highly recommend!&quot;" contentType="paragraph" pageSection="about" label="Testimonial Quote" />
              </blockquote>
              <div className="flex items-center justify-center space-x-2">
                <div className="flex space-x-1">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-4 w-4 text-[#39FF14] fill-current" />
                  ))}
                </div>
                <span className="text-[#B0B0B0] text-sm">- Verified Customer</span>
              </div>
            </div>
          </div>
        </div>

        {/* Team & Friends Section */}
        <div className="mt-20">
          <h3 className="text-3xl font-bold text-[#E0E0E0] mb-4 text-center">
            <ContentSlot slotId="about-team-heading" fallback="Meet the Team (and Friends!)" contentType="subheading" pageSection="about" label="Team Section Heading" />
          </h3>
          <ContentSlot
            slotId="about-team-subtitle"
            fallback="Behind every great repair shop is a great team... and some furry friends who keep spirits high!"
            contentType="paragraph"
            as="p"
            pageSection="about"
            label="Team Section Subtitle"
            className="text-center text-[#B0B0B0] mb-12 max-w-2xl mx-auto"
          />

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-[#00BFFF] transition-all duration-200">
              <div className="aspect-square overflow-hidden">
                <ImageSlot
                  slotId="about-team-spencer"
                  fallbackSrc="/spencer + jack.jpg"
                  fallbackAlt="Spencer"
                  className="object-cover"
                  wrapperClassName="w-full h-full"
                />
              </div>
              <div className="p-4 text-center">
                <h4 className="text-lg font-bold text-[#E0E0E0] mb-1">
                  <ContentSlot slotId="about-team-spencer-name" fallback="Spencer" contentType="label" pageSection="about" label="Team: Spencer Name" />
                </h4>
              </div>
            </div>

            <div className="bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-[#00BFFF] transition-all duration-200">
              <div className="aspect-square overflow-hidden">
                <ImageSlot
                  slotId="about-team-james"
                  fallbackSrc="/IMG_3129.jpg"
                  fallbackAlt="James"
                  className="object-cover"
                  wrapperClassName="w-full h-full"
                />
              </div>
              <div className="p-4 text-center">
                <h4 className="text-lg font-bold text-[#E0E0E0] mb-1">
                  <ContentSlot slotId="about-team-james-name" fallback="James" contentType="label" pageSection="about" label="Team: James Name" />
                </h4>
              </div>
            </div>

            <div className="bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-[#39FF14] transition-all duration-200">
              <div className="aspect-square overflow-hidden">
                <ImageSlot
                  slotId="about-team-clyde"
                  fallbackSrc="/IMG_5857.jpg"
                  fallbackAlt="Clyde the Tortoise"
                  className="object-cover"
                  wrapperClassName="w-full h-full"
                />
              </div>
              <div className="p-4 text-center">
                <h4 className="text-lg font-bold text-[#E0E0E0] mb-1">
                  <ContentSlot slotId="about-team-clyde-name" fallback="Clyde" contentType="label" pageSection="about" label="Team: Clyde Name" />
                </h4>
                <p className="text-sm text-[#B0B0B0]">
                  <ContentSlot slotId="about-team-clyde-role" fallback="Office Mascot" contentType="label" pageSection="about" label="Team: Clyde Role" />
                </p>
              </div>
            </div>

            <div className="bg-gray-900 rounded-xl overflow-hidden border border-gray-800 hover:border-[#39FF14] transition-all duration-200">
              <div className="aspect-square overflow-hidden">
                <ImageSlot
                  slotId="about-team-taz"
                  fallbackSrc="/taz.jpg"
                  fallbackAlt="Taz the Dog"
                  className="object-cover"
                  wrapperClassName="w-full h-full"
                />
              </div>
              <div className="p-4 text-center">
                <h4 className="text-lg font-bold text-[#E0E0E0] mb-1">
                  <ContentSlot slotId="about-team-taz-name" fallback="Taz" contentType="label" pageSection="about" label="Team: Taz Name" />
                </h4>
                <p className="text-sm text-[#B0B0B0]">
                  <ContentSlot slotId="about-team-taz-role" fallback="Chief Morale Officer" contentType="label" pageSection="about" label="Team: Taz Role" />
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Google Reviews Widget */}
        <div className="mt-16 text-center">
          <h4 className="text-2xl font-bold text-[#E0E0E0] mb-6">
            <ContentSlot slotId="about-reviews-heading" fallback="What Our Customers Say" contentType="subheading" pageSection="about" label="Reviews Heading" />
          </h4>
          <div className="elfsight-app-8fcf711c-ac45-45a5-a741-d8dfc7036cef" data-elfsight-app-lazy></div>
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <button
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
            className="bg-gradient-to-r from-[#FF1744] to-[#00BFFF] text-[#0D0D0D] px-8 py-4 rounded-lg text-lg font-semibold hover:from-[#39FF14] hover:to-[#00BFFF] transition-all duration-200 transform hover:scale-105 shadow-lg"
          >
            <ContentSlot slotId="about-cta" fallback="Join Our Happy Customers" contentType="button" pageSection="about" label="CTA Button" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default About;